import React, { useState, useEffect, useRef } from 'react';
import { Target, MessageSquare, BarChart, ChevronDown, Image as ImageIcon, Users, AlertCircle } from 'lucide-react';
import { generateTargeting } from '../lib/openrouter';
import RecommendationDisplay from '../components/RecommendationDisplay';
import ChatInterface from '../components/ChatInterface';
import MetricsDisplay from '../components/MetricsDisplay';
import LeadsSearch from '../components/leads/LeadsSearch';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useParams } from 'react-router-dom';
import ImageSearch from '../components/ImageSearch';

function Dashboard() {
  const { tab } = useParams();
  const [businessDescription, setBusinessDescription] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(tab || 'targeting');
  const [showRenewalMenu, setShowRenewalMenu] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastDescription, setLastDescription] = useState('');
  const [generateCalendarOnly, setGenerateCalendarOnly] = useState(false);
  const { user, profile, subscription, checkAndRefreshSession } = useAuthStore();
  const descriptionRef = useRef(null);
  const chatContainerRef = useRef(null);
  const renewalMenuRef = useRef(null);
  const navigate = useNavigate();
  const maxRetries = 100;
  const retryDelay = 1000;
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  
  const hasUserInitiatedGenerationRef = useRef(false);
  const autoRetryTimeoutRef = useRef(null);
  const isAutoRetryingRef = useRef(false);
  const lastGenerationAttemptTimeRef = useRef(0);
  
  const navigateToTab = (tabName) => {
    navigate(`/dashboard/${tabName}`);
    setActiveTab(tabName);
  };

  const handleRenewalClick = (url) => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = url;
  };

  const checkRequestLimit = () => {
    if (!subscription) return false;
    
    const isLimitedPlan = subscription.plan === 'essential' || subscription.plan === 'initial';
    if (!isLimitedPlan) return true;
    
    const hasRequestsLeft = (subscription.request_count ?? 0) > 0;
    const isSubscriptionExpired = subscription.next_billing_date && 
      new Date(new Date(subscription.next_billing_date).getTime() + 24 * 60 * 60 * 1000) < new Date();
    
    return hasRequestsLeft && !isSubscriptionExpired;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreMenu && moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  useEffect(() => {
    const savedRecommendations = localStorage.getItem('targetai_recommendations');
    if (savedRecommendations) {
      setRecommendations(JSON.parse(savedRecommendations));
    }
  }, []);

  useEffect(() => {
    if (recommendations) {
      localStorage.setItem('targetai_recommendations', JSON.stringify(recommendations));
    }
  }, [recommendations]);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    } else {
      navigate('/dashboard/targeting', { replace: true });
    }
  }, [tab, navigate]);
  
  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentTab = currentPath.split('/').pop();
    
    if (currentTab && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [activeTab]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await checkAndRefreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAndRefreshSession]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (descriptionRef.current && activeTab === 'targeting') {
      descriptionRef.current.focus();
    }
    
    if (activeTab === 'chat') {
      setTimeout(() => {
        const input = chatContainerRef.current?.querySelector('input, textarea');
        input?.focus();
      }, 0);
    }
  }, [user, navigate, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (renewalMenuRef.current && !renewalMenuRef.current.contains(event.target)) {
        setShowRenewalMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isRecommendationComplete = (result) => {
    if (!result || !result.content) return false;
    
    const content = result.content;
    
    if (generateCalendarOnly) {
      return content.includes('Calendário de Conteúdo');
    }
    
    const expectedSections = [
      '1. Persona Ideal',
      '2. Palavras-chave para Google Ads',
      '3. Interesses para Meta Ads',
      '4. Dados Demográficos',
      '5. Funis e Ofertas',
      '6. Dicas de SEO',
      '7. Recomendações de Investimento'
    ];
    
    for (const section of expectedSections) {
      if (!content.includes(section)) {
        console.log(`Seção ausente: ${section}`);
        return false;
      }
    }
    
    return true;
  };

  const handleResetComponents = () => {
    const currentRecommendations = recommendations;
    setBusinessDescription('');
    setLoading(false);
    setError('');
    setRecommendations(currentRecommendations);
    navigate('/dashboard/targeting', { replace: true });
    
    hasUserInitiatedGenerationRef.current = false;
    isAutoRetryingRef.current = false;
    if (autoRetryTimeoutRef.current) {
      clearTimeout(autoRetryTimeoutRef.current);
      autoRetryTimeoutRef.current = null;
    }
  };
  
  const handleKeyPress = async (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleGenerateRecommendations(e);
    }
  };

  const startAutoRetry = () => {
    if (isAutoRetryingRef.current || loading) {
      return;
    }
    
    if (!hasUserInitiatedGenerationRef.current || !businessDescription.trim()) {
      return;
    }
    
    if (!checkRequestLimit()) {
      console.log('Limite de consultas atingido. Parando retentativas automáticas.');
      setError('Você esgotou suas consultas mensais ou expirou a data do plano. Renove seu plano para continuar usando o serviço.');
      return;
    }
    
    if (generateCalendarOnly && recommendations?.content?.includes('Calendário de Conteúdo')) {
      return;
    }
    
    const now = Date.now();
    if (now - lastGenerationAttemptTimeRef.current < 1000) {
      autoRetryTimeoutRef.current = setTimeout(startAutoRetry, 1000);
      return;
    }
    
    console.log('Iniciando nova tentativa automática de geração...');
    isAutoRetryingRef.current = true;
    
    if (autoRetryTimeoutRef.current) {
      clearTimeout(autoRetryTimeoutRef.current);
      autoRetryTimeoutRef.current = null;
    }
    
    handleGenerateRecommendations();
  };

  useEffect(() => {
    if (!hasUserInitiatedGenerationRef.current) {
      return;
    }
    
    const hasCompleteContent = generateCalendarOnly 
      ? recommendations?.content?.includes('Calendário de Conteúdo')
      : recommendations && isRecommendationComplete(recommendations);
    
    if (!loading && businessDescription.trim() && !hasCompleteContent) {
      console.log('Condições para retentativa automática atendidas');
      
      autoRetryTimeoutRef.current = setTimeout(() => {
        startAutoRetry();
      }, 2000);
    }
    
    return () => {
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
    };
  }, [loading, recommendations, businessDescription, generateCalendarOnly]);

  useEffect(() => {
    return () => {
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
    };
  }, []);

  const handleGenerateRecommendations = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    
    if (!checkRequestLimit()) {
      console.log('Bloqueado: limite de consultas atingido antes de fazer requisição');
      setError('Você esgotou suas consultas mensais ou expirou a data do plano. Renove seu plano para continuar usando o serviço.');
      return;
    }
    
    console.log('✅ Limite verificado - prosseguindo com a geração');
    
    hasUserInitiatedGenerationRef.current = true;
    lastGenerationAttemptTimeRef.current = Date.now();
    
    setLoading(true);
    setError('');
    setRecommendations(null);
    setLastDescription(businessDescription);
    setRetryCount(0);
    
    isAutoRetryingRef.current = false;

    const generateWithRetry = async (attempt = 0) => {
      try {
        setRetryCount(attempt);
        console.log(`Tentativa ${attempt + 1} de ${maxRetries}...`);
        
        if (!checkRequestLimit()) {
          console.log(`Tentativa ${attempt + 1} bloqueada: limite atingido`);
          throw new Error('Limite de consultas atingido');
        }
        
        const result = await generateTargeting(businessDescription, generateCalendarOnly);
          
        const isComplete = generateCalendarOnly 
          ? result.content.includes('Calendário de Conteúdo')
          : isRecommendationComplete(result);
        
        if (!isComplete) {
          console.log(`Tentativa ${attempt + 1} gerou resultado incompleto.`);
          throw new Error('Resultado incompleto');
        }

        console.log(`Tentativa ${attempt + 1} bem-sucedida! Resultado completo.`);
        setRecommendations(result);
        setRetryCount(0);
        setLastDescription('');
        setError('');
        setLoading(false);
        isAutoRetryingRef.current = false;
      } catch (err) {
        console.error(`Tentativa ${attempt + 1} falhou:`, err);
        
        if (err.message === 'Limite de consultas atingido') {
          console.log('Parando todas as tentativas: limite de consultas atingido');
          setError('Você esgotou suas consultas mensais ou expirou a data do plano. Renove seu plano para continuar usando o serviço.');
          setRetryCount(0);
          setLoading(false);
          isAutoRetryingRef.current = false;
          return;
        }
        
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return generateWithRetry(attempt + 1);
        } else {
          console.log('Todas as tentativas automáticas falharam.');
          setError('Não foi possível gerar recomendações completas após várias tentativas. Tentando novamente automaticamente...');
          setRetryCount(0);
          setLoading(false);
          isAutoRetryingRef.current = false;
          
          const hasCompleteContent = generateCalendarOnly 
            ? recommendations?.content?.includes('Calendário de Conteúdo')
            : recommendations && isRecommendationComplete(recommendations);
          
          if (!hasCompleteContent) {
            autoRetryTimeoutRef.current = setTimeout(() => {
              startAutoRetry();
            }, 3000);
          }
        }
      }
    };

    generateWithRetry(0);
  };

  const toggleRenewalMenu = () => {
    setShowRenewalMenu(!showRenewalMenu);
  };

  const isSubscriptionExpired = subscription?.next_billing_date 
    ? new Date(subscription.next_billing_date) < new Date() 
    : false;

  const showLimitReached = !checkRequestLimit();
  
  const handleNavigateToPrecos = () => {
  navigate('/');
  setTimeout(() => {
    const pricingElement = document.getElementById('pricing-section');
    if (pricingElement) {
      pricingElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, 100);
};

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Painel de Controle</h1>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {subscription?.plan === 'essential' || subscription?.plan === 'initial' ? (
                    <span>Consultas restantes: {(subscription?.request_count || 0)}</span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400">Plano Ilimitado</span>
                  )}
                </div>
                <div className="text-sm">
                  {subscription?.next_billing_date && (
                    <span className="text-gray-600 dark:text-gray-300">
                      Expira em: <span className="font-bold">{new Date(subscription.next_billing_date).toLocaleDateString()}</span>
                    </span>
                  )}
                </div>
                <div className="relative" ref={renewalMenuRef}>
                  <button
                    onClick={toggleRenewalMenu}
                    className="flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
                  >
                    Renovar Agora
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showRenewalMenu ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {showRenewalMenu && (
                    <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        <button
                          onClick={() => handleRenewalClick('https://buy.stripe.com/8wM2b48uG4Or73a3co')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <div className="font-medium">Inicial</div>
                          <div className="text-gray-500 dark:text-gray-400">R$ 7,47 Ilimitado por 7 dias</div>
                        </button>
                        <button
                          onClick={() => handleRenewalClick('https://buy.stripe.com/fZe7voh1c3KnevC3cp')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <div className="font-medium">Essencial</div>
                          <div className="text-gray-500 dark:text-gray-400">R$ 17 por 30 dias e 30 consultas</div>
                        </button>
                        <button
                          onClick={() => handleRenewalClick('https://buy.stripe.com/aEU02WdP0dkXfzG7sG')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <div className="font-medium">PRO</div>
                          <div className="text-gray-500 dark:text-gray-400">R$ 27 Ilimitado por 30 dias</div>
                        </button>
                        <button
                          onClick={() => handleRenewalClick('https://buy.stripe.com/9AQcPI6mycgT3QYcN1')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <div className="font-medium">Anual</div>
                          <div className="text-gray-500 dark:text-gray-400">R$ 247 Ilimitado por 365 dias</div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {showLimitReached && (
              <>
                <div className="absolute top-20 left-0 right-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-40 h-[calc(100%-5rem)]"></div>
                <div className="absolute top-20 left-0 right-0 flex items-start justify-center z-40 p-4">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-4xl mx-auto text-center mt-8">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Você esgotou suas consultas mensais ou expirou a data do plano</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Renove seu plano para continuar usando o serviço e obter mais recomendações de segmentação.
                    </p>
                    <button
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      onClick={handleNavigateToPrecos}
                    >
                      Ver planos disponíveis
                    </button>
                  </div>
                </div>
              </>
            )}
          
            <div className={showLimitReached ? "filter blur-sm pointer-events-none" : ""}>
              <div className="mt-8 border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => navigateToTab("leads")}
                    className={`${
                      activeTab === "leads"
                        ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400"
                    } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    <Users className="h-5 w-5 mr-2" />
                    <span>Lead</span>
                  </button>
                  <button
                    onClick={() => navigateToTab("targeting")}
                    className={`${
                      activeTab === "targeting"
                        ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400"
                    } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    <Target className="h-5 w-5 mr-2" />
                    Negócio
                  </button>
                  <button
                    onClick={() => navigateToTab("chat")}
                    className={`${
                      activeTab === "chat"
                        ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400"
                    } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Chat IA
                  </button>
                  
                  <div className="relative ml-auto" ref={moreMenuRef}>
                    <button
                      onClick={() => setShowMoreMenu(!showMoreMenu)}
                      className="flex items-center text-gray-500 hover:text-gray-700 py-4 px-1"
                    >
                      Mais
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showMoreMenu ? "transform rotate-180" : ""}`} />
                    </button>
                    {showMoreMenu && (
                      <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              navigateToTab("metrics");
                              setShowMoreMenu(false);
                            }}
                            className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Predições
                          </button>
                          <button
                            onClick={() => {
                              navigateToTab("posts");
                              setShowMoreMenu(false);
                            }}
                            className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Posts
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </nav>
              </div>

              <div className="mt-8">
                {activeTab === 'targeting' && (
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Gerador de Segmentação</h2>
                    <textarea
                      ref={descriptionRef}
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Descreva seu negócio, produto ou serviço..."
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      rows={4}
                      disabled={loading || showLimitReached}
                    />
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="calendarOnly"
                          checked={generateCalendarOnly}
                          onChange={(e) => setGenerateCalendarOnly(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                          disabled={loading || showLimitReached}
                        />
                        <label htmlFor="calendarOnly" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Gerar somente Calendário de Conteúdo
                        </label>
                      </div>
                      <button
                        onClick={handleGenerateRecommendations}
                        className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${
                          loading || showLimitReached ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                        disabled={loading || !businessDescription.trim() || showLimitReached}
                      >
                        {loading ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Gerando... {retryCount > 0 ? `(Tentativa ${retryCount + 1})` : ''}
                          </div>
                        ) : 'Gerar Recomendações'}
                      </button>
                    </div>
                    {error && <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>}
                    {recommendations && (
                      <div className="mt-8">
                        <RecommendationDisplay 
                          recommendations={recommendations.content} 
                          displayMode={generateCalendarOnly ? 'calendar' : 'targeting'}
                          onRequestRetry={async () => {
                            console.log("RecommendationDisplay solicitou retentativa");
                            
                            if (!checkRequestLimit()) {
                              console.log('Retry bloqueado: limite de consultas atingido');
                              throw new Error('Limite de consultas atingido');
                            }
                            
                            const result = await generateTargeting(lastDescription || businessDescription, generateCalendarOnly);
                            return result.content;
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'chat' && (
                  <div ref={chatContainerRef}>
                    <ChatInterface />
                  </div>
                )}
                {activeTab === 'metrics' && (
                  <MetricsDisplay />
                )}
                {activeTab === 'posts' && (
                  <ImageSearch />
                )}
                {activeTab === 'leads' && (
                  <LeadsSearch />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;